import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { VELOCITY_METRICS, type VelocityFunnel, type VelocityRow } from "../config/velocity-metrics";
import type { PlanOverride } from "../config/booked-call-plan";
import { FUNNEL_LABEL, type VelocityColumn } from "../lib/velocity-sheet";
import { columnGoal, weekStartOf, weekLabel, type WeeklyCashTargets } from "../lib/cash-target";
import { SALES_MEETING_METRICS, SHEET_NAME_TWIN, WeekCashTargetInline, salesMeetingLine, type SalesMeetingLine } from "./SalesMeetingsView";

// Velocity Metrics → "Weekly breakdown" (Joey Forslund, Oct 20, 2026, 2:46 PM ET): for any PAST week column (never the
// current week), a button opens a pop-up with a short, plain explanation of which metrics were up and which were down
// against that week's targets — "this metric was down, but this metric was up, so we upheld our KPI". Every number
// is the regular Velocity number for the column (same /api/velocity-metrics row) scored the way the Sales Meetings
// View scores a week: targets derive from the week's cash collected target (a weekly override, else the monthly goal ÷
// 4.33). Changing the week's target here rewrites the breakdown on the spot. Pure arithmetic, no model: the same
// inputs always give the same words.
// Oct 20, 2026, 3:12 PM ET (Joey): "more of a bullet point breakdown … hard to read all the metrics in a paragraph view"
// and "on the sales meetings view as well". So: one headline line, one bullet per metric (Below / On or above target),
// and 2–3 short takeaway bullets; the same pop-up opens from the Sales Meetings View's past week with its own sections
// (Total / Book a call / Webinar) and its full connected metric list.

/** The metrics the breakdown reads from the main Velocity list, in Joey's order (the first block of the Sales Meetings
 *  View; the Book a call sheet's "not connected" rows are left out). */
const BREAKDOWN_METRICS = SALES_MEETING_METRICS.filter(([, metricLabel]) => !VELOCITY_METRICS.find((m) => m.label === metricLabel)?.notConnected).slice(0, 21);
/** Top-of-funnel and activity counts: "how much came in / happened". */
const VOLUME = new Set(["Full Applications", "Booked Calls", "Book Calls Outbound", "Total Booked Closing Calls (All)", "EOCs", "Showed Calls", "Closed Deals"]);
/** Conversion rates and per-unit value: "how well we turned it into cash". */
const CONVERSION = new Set(["Show Rate", "Close Rate", "Lead to Close %", "Closing Call Booked To Close %"]);
const VALUE = new Set(["Average Sale Price", "Avg Cash / Call", "Average Cash per Application", "Average Cash per Booked Call", "Cash VS Rev %"]);

/** From the Sales Meetings View: every connected Sales Meetings row, the Book a call sheet's twin names counted once. */
const SALES_MEETING_BREAKDOWN_METRICS = (() => {
  const seen = new Set<string>();
  return SALES_MEETING_METRICS.filter(([, metricLabel]) => {
    if (VELOCITY_METRICS.find((m) => m.label === metricLabel)?.notConnected) return false;
    const key = SHEET_NAME_TWIN[metricLabel] ?? metricLabel;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
})();

const GREEN = "#16a34a"; const RED = "#dc2626"; const GRAY = "#9ca3af";

const absDiff = (line: SalesMeetingLine) => line.diff.replace(/^[+−±]/, "");
/** Cancellations, Refunds and Partial applications are caps: under the line is the good side. */
const isCap = (line: SalesMeetingLine) => /^under /.test(line.target);
/** "≤$1,598" out of "under 5% of cash (≤$1,598)"; the rule itself when no number is attached. */
const capOf = (line: SalesMeetingLine) => line.target.match(/\((≤[^)]+)\)/)?.[1] ?? line.target.replace(/^under /, "≤");
/** One metric per bullet: "Applications (full): 49 vs 101 (−52)" · "Refunds: $0 vs cap ≤$1,598 ($1,598 under)". */
export const metricBullet = (line: SalesMeetingLine) => isCap(line)
  ? `${line.label}: ${line.actual} vs cap ${capOf(line)}${line.diffValue === null ? "" : ` (${absDiff(line)} ${line.diffValue <= 0 ? "under" : "over"})`}`
  : `${line.label}: ${line.actual} vs ${line.target}${line.diffValue === null ? "" : ` (${line.diff})`}`;
/** "applications (full) −52" — the short form the takeaway bullets use. */
const short = (line: SalesMeetingLine) => `${line.label.replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase())} ${isCap(line) ? `${line.actual} (cap ${capOf(line)})` : `${line.diff}`}`;
/** At most two named, the rest counted, so each takeaway stays one short line. */
const few = (lines: SalesMeetingLine[], max = 2) => lines.slice(0, max).map(short).join(", ") + (lines.length > max ? ` (+${lines.length - max} more)` : "");

export type WeeklyBreakdownBullets = { headline: string; kpi: boolean | null; downs: SalesMeetingLine[]; ups: SalesMeetingLine[]; unscored: SalesMeetingLine[]; takeaways: string[] };

/**
 * The week as bullets, from the scored lines alone. Deterministic.
 * Headline: cash collected vs the weekly target (the KPI), held or missed.
 * Takeaways (2–3): what volume did, what conversion / deal value did, and the result.
 */
export function weeklyBreakdownBullets(lines: SalesMeetingLine[]): WeeklyBreakdownBullets {
  const cash = lines.find((line) => line.metric.label === "Cash Collected");
  const scored = lines.filter((line) => line.good !== null);
  // Cash collected is the headline, so the lists leave it out.
  const listed = scored.filter((line) => line !== cash);
  const downs = listed.filter((line) => line.good === false);
  const ups = listed.filter((line) => line.good === true);
  const unscored = lines.filter((line) => line.good === null);
  const kpi = cash?.good ?? null;
  const headline = kpi === null || !cash
    ? "Cash collected: no weekly target to score against"
    : `Cash collected: ${cash.actual} vs ${cash.target} weekly target (${cash.diff}) · KPI ${kpi ? "held" : "missed"}`;
  const isVol = (line: SalesMeetingLine) => VOLUME.has(line.metric.label);
  const isConv = (line: SalesMeetingLine) => CONVERSION.has(line.metric.label) || VALUE.has(line.metric.label);
  const volDown = downs.filter(isVol); const volUp = ups.filter(isVol);
  const convDown = downs.filter(isConv); const convUp = ups.filter(isConv);
  const takeaways: string[] = [];
  if (kpi === null) {
    takeaways.push(listed.length ? `${ups.length} of ${listed.length} scored metrics on or above target` : "Nothing this week has a target to score against");
    return { headline, kpi, downs, ups, unscored, takeaways };
  }
  if (volDown.length) takeaways.push(`Volume short: ${few(volDown)}`);
  else if (volUp.length) takeaways.push(`Volume on target: ${few(volUp)}`);
  if (kpi) {
    if (convUp.length) takeaways.push(`${volDown.length ? "Conversion carried it" : "Conversion up"}: ${few(convUp)}`);
    else if (convDown.length) takeaways.push(`Conversion short: ${few(convDown)}`);
    takeaways.push(`Result: cash KPI held, ${absDiff(cash!)} over target`);
  } else {
    if (convDown.length) takeaways.push(`Conversion short: ${few(convDown)}`);
    else if (convUp.length) takeaways.push(`Conversion up but not enough: ${few(convUp)}`);
    const gap = volDown.length && convDown.length ? "volume and conversion both" : volDown.length ? "volume" : convDown.length ? "conversion" : "";
    takeaways.push(gap
      ? `Result: cash ${absDiff(cash!)} short; ${gap} drove the miss`
      : `Result: cash ${absDiff(cash!)} short with other metrics on target (check closes paid in a later week)`);
  }
  return { headline, kpi, downs, ups, unscored, takeaways: takeaways.slice(0, 3) };
}

type Props = {
  column: VelocityColumn; monthlyTarget: number; weeklyTargets: WeeklyCashTargets; overrides: Record<string, PlanOverride>; onClose: () => void;
  /** Main Velocity list: the page's funnel switch drives the pop-up. Sales Meetings View: the pop-up keeps its own. */
  funnel?: VelocityFunnel; onFunnel?: (funnel: VelocityFunnel) => void;
  /** Which view opened it: names the sections the way that view does and picks its metric list. */
  source?: "velocity" | "salesMeetings";
};

const FUNNELS: VelocityFunnel[] = ["blended", "bookCall", "webinar"];
const SHORT_LABEL: Record<"velocity" | "salesMeetings", Record<VelocityFunnel, string>> = {
  velocity: { blended: "Blended", bookCall: "Book a call funnel", webinar: "Webinar funnel" },
  salesMeetings: { blended: "Total", bookCall: "Book a call funnel", webinar: "Webinar funnel" },
};
const SUBTITLE: Record<"velocity" | "salesMeetings", Record<VelocityFunnel, string>> = {
  velocity: FUNNEL_LABEL,
  salesMeetings: { blended: "Sales Meetings View · Total (Book a call + Webinar)", bookCall: "Sales Meetings View · Book a call funnel", webinar: "Sales Meetings View · Webinar funnel" },
};

function BulletList({ items, color, testId }: { items: string[]; color: string; testId: string }) {
  return <ul className="mt-1 space-y-0.5 text-[13px] leading-snug text-foreground" data-weekly-breakdown-list={testId}>
    {items.map((item) => <li key={item} className="flex gap-2"><span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} /><span className="tabular-nums">{item}</span></li>)}
  </ul>;
}

export function WeeklyBreakdown({ column, funnel, onFunnel, monthlyTarget, weeklyTargets, overrides, onClose, source = "velocity" }: Props) {
  const [ownFunnel, setOwnFunnel] = useState<VelocityFunnel>(funnel ?? "blended");
  const activeFunnel: VelocityFunnel = onFunnel && funnel ? funnel : ownFunnel;
  const pickFunnel = (key: VelocityFunnel) => { if (onFunnel) onFunnel(key); else setOwnFunnel(key); };
  const week = weekStartOf(column.startDate);
  const fullWeek = column.days === 7 && column.startDate === week;
  const weekText = fullWeek ? weekLabel(week) : column.label;
  // The column's goal: its weekly cash override wins, else the monthly goal. Recomputed whenever a target is saved.
  const goal = columnGoal(column.startDate, column.endDate, monthlyTarget, weeklyTargets);
  const row: VelocityRow = column[activeFunnel];
  const metrics = source === "salesMeetings" ? SALES_MEETING_BREAKDOWN_METRICS : BREAKDOWN_METRICS;
  const lines = useMemo(() => metrics.map(([label, metricLabel]) => salesMeetingLine(label, VELOCITY_METRICS.find((m) => m.label === metricLabel)!, row, activeFunnel, goal, overrides, null, column.days)), [metrics, row, activeFunnel, goal, overrides, column.days]);
  const story = useMemo(() => weeklyBreakdownBullets(lines), [lines]);
  const [showAll, setShowAll] = useState(false);
  const kpiColor = story.kpi === null ? GRAY : story.kpi ? GREEN : RED;
  const heading = (text: string, color: string, count: number) => <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color }}>{text} <span className="font-normal normal-case tracking-normal text-muted-foreground">· {count}</span></p>;

  return <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={`Weekly breakdown ${weekText}`} onClick={onClose} data-weekly-breakdown={source}>
    <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl border border-border bg-card p-5 shadow-xl" onClick={(event) => event.stopPropagation()}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-foreground">Weekly breakdown · {weekText}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{SUBTITLE[source][activeFunnel]}{fullWeek ? " · Mon–Sun, Eastern" : ` · ${column.days} of 7 days, targets scaled to the days shown`}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md border border-border p-1 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5" role="group" aria-label="Funnel">
          {FUNNELS.map((key) => <button key={key} type="button" onClick={() => pickFunnel(key)} className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${activeFunnel === key ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`} data-weekly-breakdown-funnel={key}>{SHORT_LABEL[source][key]}</button>)}
        </div>
        <WeekCashTargetInline key={week} week={week} monthlyTarget={monthlyTarget} weeklyTargets={weeklyTargets} note="Change it and the breakdown recalculates." />
      </div>

      <div className="mt-3 rounded-lg border px-3 py-2" style={{ borderColor: `${kpiColor}66`, borderLeftWidth: 4, borderLeftColor: kpiColor }} data-weekly-breakdown-story>
        <p className="text-sm font-semibold tabular-nums text-foreground" data-weekly-breakdown-headline>{story.headline}</p>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          {heading("Below target", RED, story.downs.length)}
          {story.downs.length ? <BulletList items={story.downs.map(metricBullet)} color={RED} testId="below" /> : <p className="mt-1 text-[13px] text-muted-foreground">Nothing below target</p>}
        </div>
        <div>
          {heading("On or above target", GREEN, story.ups.length)}
          {story.ups.length ? <BulletList items={story.ups.map(metricBullet)} color={GREEN} testId="above" /> : <p className="mt-1 text-[13px] text-muted-foreground">Nothing on or above target</p>}
        </div>
      </div>

      <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2" data-weekly-breakdown-why>
        {heading("Takeaways", "var(--brand-primary)", story.takeaways.length)}
        <BulletList items={story.takeaways} color="var(--brand-primary)" testId="takeaways" />
      </div>

      {story.unscored.length ? <>
        <button type="button" onClick={() => setShowAll((on) => !on)} className="mt-2 text-[11px] text-muted-foreground underline">{showAll ? "Hide" : "Show"} the {story.unscored.length} metrics with no target this week</button>
        {showAll ? <BulletList items={story.unscored.map((line) => `${line.label}: ${line.actual}`)} color={GRAY} testId="unscored" /> : null}
      </> : null}
      <p className="mt-2 text-[10px] text-muted-foreground">Same numbers as the {source === "salesMeetings" ? "Sales Meetings View" : "column above"} (bookings by booking time, EOCs / shows / closes by call date, cash on its payment date, refunds by refund date). Targets are the Velocity targets for the week's cash collected target; Cancellations, Refunds and Partial applications are caps (lower is better).</p>
    </div>
  </div>;
}
